import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Pin, PinOff, Lock, Unlock, FolderInput, ShieldBan, X, Loader2 } from "lucide-react";

const DURATIONS = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "permanent", label: "Permanent" },
];

// Inline moderator toolbar rendered above a thread's OP (mods/admins only). Pin, Lock, Move, and
// Restrict-the-author — all forum-scoped.
export function ModToolbar({ P, thread, categories, onChanged, onRestrict }) {
  const [busy, setBusy] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const act = async (key, fn) => {
    setBusy(key);
    try { await fn(); await onChanged(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const pin = () => act("pin", async () => {
    await api.post(`/forum/mod/threads/${thread.thread_id}/pin`, { pinned: !thread.pinned });
    toast.success(thread.pinned ? "Unpinned." : "Pinned.");
  });
  const lock = () => act("lock", async () => {
    await api.post(`/forum/mod/threads/${thread.thread_id}/lock`, { locked: !thread.locked });
    toast.success(thread.locked ? "Unlocked." : "Locked.");
  });
  const move = (slug) => act("move", async () => {
    await api.post(`/forum/mod/threads/${thread.thread_id}/move`, { category_slug: slug });
    setMoveOpen(false); toast.success("Thread moved.");
  });
  const btn = "inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-wide px-2.5 py-1.5 border transition-colors disabled:opacity-50";
  const style = { color: P.text, borderColor: P.cardBorder, background: P.rowSurface };
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3 p-2 border" style={{ borderColor: P.cardBorder, background: "rgba(0,0,0,0.25)" }} data-testid="forum-mod-toolbar">
      <span className="font-mono text-[10px] uppercase tracking-[1.5px] px-1" style={{ color: P.lime }}>Mod</span>
      <button onClick={pin} disabled={!!busy} className={btn} style={style} data-testid="forum-mod-pin">
        {thread.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}{thread.pinned ? "Unpin" : "Pin"}
      </button>
      <button onClick={lock} disabled={!!busy} className={btn} style={style} data-testid="forum-mod-lock">
        {thread.locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}{thread.locked ? "Unlock" : "Lock"}
      </button>
      <div className="relative">
        <button onClick={() => setMoveOpen((v) => !v)} disabled={!!busy} className={btn} style={style} data-testid="forum-mod-move">
          <FolderInput className="w-3.5 h-3.5" /> Move
        </button>
        {moveOpen && (
          <div className="absolute z-20 mt-1 min-w-[180px] border shadow-xl" style={{ background: P.rowSurface, borderColor: P.cardBorder }} data-testid="forum-mod-move-menu">
            {categories.map((c) => (
              <button key={c.slug} onClick={() => move(c.slug)} disabled={c.slug === thread.category_slug}
                data-testid={`forum-mod-move-${c.slug}`}
                className="block w-full text-left font-mono text-xs px-3 py-2 hover:opacity-80 disabled:opacity-40"
                style={{ color: c.slug === thread.category_slug ? P.lime : P.text }}>
                {c.label}{c.slug === thread.category_slug ? " ✓" : ""}
              </button>
            ))}
          </div>
        )}
      </div>
      <button onClick={onRestrict} className={btn} style={{ color: "#e0736a", borderColor: "#e0736a55", background: P.rowSurface }} data-testid="forum-mod-restrict">
        <ShieldBan className="w-3.5 h-3.5" /> Restrict @{thread.author_username}
      </button>
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: P.textMuted }} />}
    </div>
  );
}

// Mute/ban dialog for a specific member (forum-scoped).
export function RestrictDialog({ P, authorUsername, authorId, onClose, onDone }) {
  const [kind, setKind] = useState("mute");
  const [duration, setDuration] = useState("24h");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 3) { toast.error("Add a short reason (for the audit log)."); return; }
    setBusy(true);
    try {
      await api.post("/forum/mod/restrict", { user_id: authorId, kind, duration, reason: reason.trim() });
      toast.success(`@${authorUsername} ${kind === "ban" ? "banned" : "muted"} (${duration}).`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const pill = (active) => active
    ? { color: "#fff", background: P.lime, borderColor: P.lime, filter: "brightness(0.9)" }
    : { color: P.text, borderColor: P.cardBorder };
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="forum-restrict-dialog">
      <div className="w-full max-w-sm border" style={{ background: P.rowSurface, borderColor: P.cardBorder }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: P.cardBorder }}>
          <h3 className="font-heading uppercase tracking-tight flex items-center gap-2" style={{ color: P.text }}><ShieldBan className="w-4 h-4" style={{ color: "#e0736a" }} /> Restrict @{authorUsername}</h3>
          <button onClick={onClose} data-testid="forum-restrict-close" style={{ color: P.textMuted }}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            {["mute", "ban"].map((k) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`forum-restrict-kind-${k}`} className="flex-1 font-mono text-xs uppercase px-3 py-2 border" style={pill(kind === k)}>{k}</button>
            ))}
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wide block mb-1" style={{ color: P.textMuted }}>Duration</label>
            <div className="grid grid-cols-2 gap-2">
              {DURATIONS.map((d) => (
                <button key={d.key} onClick={() => setDuration(d.key)} data-testid={`forum-restrict-duration-${d.key}`} className="font-mono text-xs px-3 py-2 border" style={pill(duration === d.key)}>{d.label}</button>
              ))}
            </div>
          </div>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} placeholder="Reason (logged)" data-testid="forum-restrict-reason"
            className="w-full bg-black/40 border px-3 py-2 text-sm resize-y outline-none" style={{ color: P.text, borderColor: P.cardBorder }} />
          <button onClick={submit} disabled={busy} data-testid="forum-restrict-submit"
            className="w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 text-white transition-colors disabled:opacity-50"
            style={{ background: "#a33d28" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldBan className="w-4 h-4" />} Apply restriction
          </button>
        </div>
      </div>
    </div>
  );
}
